import { useCallback, useEffect, useRef, useState } from 'react';
import { useScrapingProgress, type EntityLogEntry } from '../../hooks/useScrapingProgress';
import { useScraping } from '../../contexts/ScrapingContext';
import { useProperty } from '../../contexts/PropertyContext';
import { apiService } from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { Badge } from './Badge';
import { Button } from './Button';
import { getCategoryLabel } from '../../utils/categoryLabels';
import { getCategoryColor } from '../../utils/categoryColors';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertCircle,
  WifiOff,
  Search,
  Globe,
  Brain,
  Database,
  ChevronDown,
  ChevronUp,
  Merge,
  Plus,
  Circle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Phase timeline
// ---------------------------------------------------------------------------

interface PhaseStep {
  key: string;
  label: string;
  icon: typeof Search;
  /** Status values that indicate this phase is active */
  activeStatuses: string[];
  /** Status values that indicate this phase is done */
  doneStatuses: string[];
}

const PHASE_TIMELINE: PhaseStep[] = [
  {
    key: 'discover',
    label: 'Discovering pages',
    icon: Search,
    activeStatuses: ['initializing', 'discovering'],
    doneStatuses: ['crawling', 'classifying', 'analyzing', 'completed'],
  },
  {
    key: 'crawl',
    label: 'Scraping content',
    icon: Globe,
    activeStatuses: ['crawling'],
    doneStatuses: ['classifying', 'analyzing', 'completed'],
  },
  {
    key: 'analyze',
    label: 'AI analysis',
    icon: Brain,
    activeStatuses: ['classifying', 'analyzing'],
    doneStatuses: ['completed'],
  },
  {
    key: 'store',
    label: 'Storing entities',
    icon: Database,
    activeStatuses: [], // sub-phase of analyzing
    doneStatuses: ['completed'],
  },
];

export const PHASE_LABELS: Record<string, string> = {
  queued: 'Waiting to start...',
  initializing: 'Initializing...',
  discovering: 'Discovering pages...',
  crawling: 'Scraping pages...',
  classifying: 'Classifying pages...',
  analyzing: 'AI analyzing content...',
  completed: 'Completed',
  failed: 'Failed',
};

// Category colors imported from utils/categoryColors.ts

// ---------------------------------------------------------------------------
// Phase timeline row
// ---------------------------------------------------------------------------

function PhaseRow({
  step,
  status,
  detail,
  isLast,
}: {
  step: PhaseStep;
  status: 'pending' | 'active' | 'done';
  detail?: string;
  isLast: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      {/* Icon column with connecting line */}
      <div className="flex flex-col items-center">
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
          status === 'done'
            ? 'bg-green-100'
            : status === 'active'
              ? 'bg-primary-100'
              : 'bg-surface-100'
        }`}>
          {status === 'done' ? (
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          ) : status === 'active' ? (
            <Loader2 className="w-4 h-4 text-primary-600 animate-spin" />
          ) : (
            <Circle className="w-3 h-3 text-surface-400" />
          )}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-5 ${status === 'done' ? 'bg-green-200' : 'bg-surface-200'}`} />
        )}
      </div>
      {/* Label */}
      <div className="pt-1">
        <span className={`text-sm ${
          status === 'done'
            ? 'text-green-700'
            : status === 'active'
              ? 'text-primary-800 font-medium'
              : 'text-surface-400'
        }`}>
          {step.label}
        </span>
        {detail && status !== 'pending' && (
          <span className="text-xs text-surface-500 ml-2">{detail}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entity Feed Panel
// ---------------------------------------------------------------------------

function EntityFeedPanel({ entries, variant }: { entries: EntityLogEntry[]; variant: 'active' | 'complete' }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries.length]);

  if (entries.length === 0) return null;

  const newCount = entries.filter((e) => !e.merged).length;
  const mergedCount = entries.filter((e) => e.merged).length;
  const borderColor = variant === 'active' ? 'border-primary-200' : 'border-green-200';
  const headerColor = variant === 'active' ? 'text-primary-700' : 'text-green-700';
  const countColor = variant === 'active' ? 'text-primary-600' : 'text-green-600';

  return (
    <div className={`mt-3 border-t ${borderColor} pt-3`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${headerColor}`}>
          Entities ({entries.length})
        </span>
        <div className={`flex items-center gap-2 text-xs ${countColor}`}>
          {newCount > 0 && (
            <span className="flex items-center gap-1">
              <Plus className="w-3 h-3" />
              {newCount} new
            </span>
          )}
          {mergedCount > 0 && (
            <span className="flex items-center gap-1">
              <Merge className="w-3 h-3" />
              {mergedCount} updated
            </span>
          )}
        </div>
      </div>
      <div ref={scrollRef} className="max-h-40 overflow-y-auto space-y-1 scrollbar-thin">
        {entries.map((entry, i) => (
          <div
            key={`${entry.name}-${i}`}
            className="flex items-center gap-2 py-1 px-2 rounded text-xs bg-white/60"
          >
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                getCategoryColor(entry.category)
              }`}
            >
              {getCategoryLabel(entry.category)}
            </span>
            <span className="text-surface-800 truncate flex-1">{entry.name}</span>
            {entry.merged ? (
              <Badge variant="secondary" size="sm">updated</Badge>
            ) : (
              <Badge variant="primary" size="sm">new</Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ScrapingProgressBannerProps {
  propertyId: string;
  onComplete?: () => void;
  showStartButton?: boolean;
  compact?: boolean;
}

export function ScrapingProgressBanner({
  propertyId,
  onComplete,
  showStartButton = false,
  compact = false,
}: ScrapingProgressBannerProps) {
  const { startScraping, trackExistingJob, getJobId } = useScraping();
  const { selectedProperty } = useProperty();
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [feedExpanded, setFeedExpanded] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(false);

  const jobId = getJobId(propertyId);

  // Reset check flag when property changes
  const checkedRef = useRef(false);
  const prevPropertyRef = useRef(propertyId);
  if (prevPropertyRef.current !== propertyId) {
    prevPropertyRef.current = propertyId;
    checkedRef.current = false;
  }

  // Check for existing active job on mount or property change
  const checkForActiveJob = useCallback(async () => {
    setChecking(true);
    setCheckError(false);
    try {
      const data = await apiService.get<{ active: boolean; job: { jobId: string; status: string } | null; error?: string }>(
        API_CONFIG.endpoints.knowledgeBase.scrapingStatus(propertyId),
      );
      if (data.error) {
        setCheckError(true);
      } else if (data.active && data.job?.jobId) {
        trackExistingJob(propertyId, data.job.jobId, selectedProperty?.name);
      }
    } catch {
      setCheckError(true);
    } finally {
      setChecking(false);
    }
  }, [propertyId, trackExistingJob, selectedProperty?.name]);

  useEffect(() => {
    if (checkedRef.current || jobId) return;
    checkedRef.current = true;
    checkForActiveJob();
  }, [propertyId, jobId, checkForActiveJob]);

  const progress = useScrapingProgress(jobId);

  // Suppress UI flash: jobId is known but WebSocket hasn't confirmed state yet.
  // Time-box to 15s to avoid permanent "Connecting..." if WebSocket fails.
  const jobIdSetAtRef = useRef<number | null>(null);
  const [connectionTimedOut, setConnectionTimedOut] = useState(false);
  if (jobId && jobIdSetAtRef.current === null) {
    jobIdSetAtRef.current = Date.now();
  } else if (!jobId) {
    jobIdSetAtRef.current = null;
  }
  useEffect(() => {
    if (!jobId || progress.active || progress.completed || progress.failed) {
      setConnectionTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setConnectionTimedOut(true), 15_000);
    return () => clearTimeout(timer);
  }, [jobId, progress.active, progress.completed, progress.failed]);

  const awaitingConnection = !!jobId && !progress.active && !progress.completed && !progress.failed && !connectionTimedOut;

  // Detect completion transition
  const prevCompletedRef = useRef(false);
  useEffect(() => {
    if (progress.completed && !prevCompletedRef.current) {
      prevCompletedRef.current = true;
      onComplete?.();
    }
  }, [progress.completed, onComplete]);

  // Reset completion tracking when jobId changes
  useEffect(() => {
    prevCompletedRef.current = false;
  }, [jobId]);

  const handleStartScraping = async () => {
    setStartError(null);
    setStarting(true);
    try {
      await startScraping(propertyId, selectedProperty?.name);
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'Failed to start scraping');
    } finally {
      setStarting(false);
    }
  };

  // Determine phase status for timeline
  const currentStatus = progress.status || '';
  const isStoring = progress.phase?.toLowerCase().includes('storing') ?? false;
  const hasEntityLog = progress.entityLog.length > 0;
  const failedPages = progress.pageStatuses.filter((p) => p.status === 'failed');

  const etaLabel = (() => {
    if (!progress.active || progress.etaMs === null) return null;
    const remainingMs = progress.etaMs - Date.now();
    if (remainingMs <= 0) return 'almost done';
    const minutes = Math.round(remainingMs / 60_000);
    return minutes < 1 ? 'under a minute left' : `~${minutes} min left`;
  })();

  function getPhaseStatus(step: PhaseStep): 'pending' | 'active' | 'done' {
    if (step.key === 'store') {
      // Special: storing is a sub-phase of analyzing
      if (progress.completed || progress.failed) return hasEntityLog ? 'done' : 'pending';
      if (isStoring || hasEntityLog) return isStoring ? 'active' : 'done';
      return 'pending';
    }
    if (step.doneStatuses.includes(currentStatus)) return 'done';
    if (step.activeStatuses.includes(currentStatus)) return 'active';
    return 'pending';
  }

  function getPhaseDetail(step: PhaseStep): string | undefined {
    switch (step.key) {
      case 'discover':
        if (progress.totalUrls > 0) return `${progress.totalUrls} URLs found`;
        return undefined;
      case 'crawl':
        if (progress.processedUrls > 0 && progress.totalUrls > 0)
          return `${progress.processedUrls}/${progress.totalUrls} pages`;
        return progress.currentUrl ? progress.currentUrl.replace(/^https?:\/\//, '') : undefined;
      case 'analyze':
        // Surface AI sub-phases (PDF extraction, classification, deep extraction)
        if (!isStoring && (currentStatus === 'classifying' || currentStatus === 'analyzing')) {
          return progress.phase ?? undefined;
        }
        return undefined;
      case 'store':
        if (progress.entitiesExtracted > 0) return `${progress.entitiesExtracted} entities`;
        return undefined;
      default:
        return undefined;
    }
  }

  return (
    <div className="space-y-3">
      {/* Active scraping progress */}
      {progress.active && (
        <div className={`bg-primary-50 border border-primary-200 rounded-lg ${compact ? 'p-3' : 'p-4'}`}>
          {/* Progress bar */}
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-medium text-primary-900">
              Populating Knowledge Base...
            </h4>
            <div className="flex items-center gap-2">
              {etaLabel && <span className="text-xs text-primary-500">{etaLabel}</span>}
              <span className="text-sm font-medium text-primary-700">{Math.round(progress.percentage)}%</span>
            </div>
          </div>
          <div className="w-full bg-primary-100 rounded-full h-2 mb-4">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.max(progress.percentage, 2)}%` }}
            />
          </div>

          {/* Phase timeline */}
          {!compact && (
            <div className="ml-1 mb-1">
              {PHASE_TIMELINE.map((step, i) => (
                <PhaseRow
                  key={step.key}
                  step={step}
                  status={getPhaseStatus(step)}
                  detail={getPhaseDetail(step)}
                  isLast={i === PHASE_TIMELINE.length - 1}
                />
              ))}
            </div>
          )}

          {/* Connection status */}
          {!compact && !progress.connected && (
            <div className="flex items-center gap-1 mt-2 text-xs text-amber-600">
              <WifiOff className="w-3 h-3" />
              <span>Reconnecting...</span>
            </div>
          )}

          {/* Entity feed panel */}
          {!compact && hasEntityLog && (
            <div>
              <button
                type="button"
                onClick={() => setFeedExpanded((prev) => !prev)}
                className="flex items-center gap-1 mt-2 text-xs text-primary-600 hover:text-primary-800"
              >
                {feedExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {feedExpanded ? 'Hide' : 'Show'} entity details
              </button>
              {feedExpanded && <EntityFeedPanel entries={progress.entityLog} variant="active" />}
            </div>
          )}
        </div>
      )}

      {/* Completed summary */}
      {progress.completed && (
        <div className="border rounded-lg p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-medium text-green-800">
                Knowledge Base populated
                {progress.entitiesExtracted > 0 && ` — ${progress.entitiesExtracted} entities`}
                {progress.scrapedPageTitles.length > 0 && ` from ${progress.scrapedPageTitles.length} pages`}
                {progress.pdfsExtracted > 0 && ` (incl. ${progress.pdfsExtracted} PDF${progress.pdfsExtracted === 1 ? '' : 's'})`}
              </span>
              {progress.entitiesExtracted === 0 && progress.errors.length > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  Some pages could not be scraped. Try again or check the website URL in Settings.
                </p>
              )}
              {failedPages.length > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  {failedPages.length} page{failedPages.length === 1 ? '' : 's'} could not be scraped after retry:{' '}
                  {failedPages.slice(0, 3).map((p) => p.url.replace(/^https?:\/\/[^/]+/, '')).join(', ')}
                  {failedPages.length > 3 && '…'}
                </p>
              )}
              {hasEntityLog && (() => {
                const newCount = progress.entityLog.filter((e) => !e.merged).length;
                const mergedCount = progress.entityLog.filter((e) => e.merged).length;
                return (
                  <div className="mt-1 flex items-center gap-3 text-xs text-green-700">
                    {newCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Plus className="w-3 h-3" />
                        {newCount} new
                      </span>
                    )}
                    {mergedCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Merge className="w-3 h-3" />
                        {mergedCount} updated
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          {!compact && hasEntityLog && (
            <div>
              <button
                type="button"
                onClick={() => setFeedExpanded((prev) => !prev)}
                className="flex items-center gap-1 mt-2 ml-8 text-xs text-green-600 hover:text-green-800"
              >
                {feedExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {feedExpanded ? 'Hide' : 'Show'} details
              </button>
              {feedExpanded && (
                <div className="ml-8">
                  <EntityFeedPanel entries={progress.entityLog} variant="complete" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Failed summary */}
      {progress.failed && (
        <div className="border rounded-lg p-3 flex items-center gap-3 bg-red-50 border-red-200">
          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-800">
            Scraping failed
            {progress.errors.length > 0 && `: ${progress.errors[0]}`}
          </span>
        </div>
      )}

      {/* Start error */}
      {startError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-800">{startError}</span>
        </div>
      )}

      {/* Checking for active job or awaiting WebSocket connection */}
      {(checking || awaitingConnection) && (
        <div className="flex items-center gap-2 text-sm text-surface-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{awaitingConnection ? 'Connecting to scraping job...' : 'Checking for active scraping job...'}</span>
        </div>
      )}

      {/* Status check error */}
      {checkError && !progress.active && !checking && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800">Could not check scraping status</span>
          <button
            type="button"
            onClick={() => { checkedRef.current = false; checkForActiveJob(); }}
            className="ml-auto text-sm font-medium text-amber-700 hover:text-amber-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Start button */}
      {showStartButton && !progress.active && !starting && !checking && !awaitingConnection && (
        <Button
          variant="primary"
          icon={<RefreshCw className="w-4 h-4" />}
          onClick={handleStartScraping}
          disabled={starting}
        >
          {progress.completed ? 'Re-scan Website' : 'Populate Knowledge Base'}
        </Button>
      )}
    </div>
  );
}
